import { useState } from "react";
import type { ComponentProps } from "react";
import { HtImage } from "@hightouch/htui";
import { useUser } from "src/contexts/user-context";
import { useRegionConfig } from "src/utils/use-region-config";
import { buildContentImageThumbnailUrl } from "./utils";

// `HtImage`'s props are a discriminated union on `alt | decorative`; thumbnails
// always have an alt, so we narrow to the alt branch and drop `src` (we own it).
type HtImageWithAltProps = Extract<
  ComponentProps<typeof HtImage>,
  { alt: string }
>;

type Props = Omit<HtImageWithAltProps, "src"> & {
  imageId: string;
  presignedUrl: string;
  // Pixel width hint passed to imgproxy. Distinct from the style `width` prop,
  // which controls the rendered element size.
  thumbnailWidth: number;
};

// Tries the imgproxy-resized thumbnail first and falls back to the full
// presigned URL on error. We've seen some 422s from imgproxy
export const ContentImageThumbnail = ({
  imageId,
  presignedUrl,
  thumbnailWidth,
  ...imageProps
}: Props) => {
  const { workspace } = useUser();
  const { apiEndpoint } = useRegionConfig();

  const thumbnailUrl = workspace?.id
    ? buildContentImageThumbnailUrl({
        apiEndpoint,
        workspaceId: workspace.id,
        imageId,
        width: thumbnailWidth,
      })
    : presignedUrl;

  // Track failed URLs so a new imageId / regenerated thumbnail URL resets
  // automatically without needing a `key` on the parent.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const src = failedUrl === thumbnailUrl ? presignedUrl : thumbnailUrl;

  return (
    <HtImage
      {...imageProps}
      src={src}
      // Native <img> drags start after a few pixels and the catalog dropzone
      // would treat them as uploads. Same approach as Ad Studio canvases.
      draggable={false}
      onError={(e) => {
        if (src === thumbnailUrl) setFailedUrl(thumbnailUrl);
        imageProps.onError?.(e);
      }}
    />
  );
};
